import React, { useEffect, useRef, useState } from "react";
import { Activity, LogOut, User as UserIcon, Shield, Zap } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function MainSwitch({ active, onSwitch }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const indicatorColor = active === 1 ? "#FFCC00" : active === 2 ? "#F472B6" : "#60A5FA";
  const indicatorLabel = active === 1
    ? "Part 1 · NAVA-INTRA · India Intraday"
    : active === 2 ? "Part 2 · NAVA-US · US Intraday"
    : "Admin Console · Owner";

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-zinc-900">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        {/* Brand */}
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <div className="relative w-9 h-9 rounded-md bg-[#FFCC00] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-black"><path d="M3 17 L9 11 L13 14 L21 6" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold text-base tracking-tight">TradingExpert AI</span>
            <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">India + US Intraday · NSE • NASDAQ • NYSE</span>
          </div>
        </div>

        {/* Tab switch */}
        <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-full p-1" data-testid="main-switch">
          <SwitchBtn testid="main-switch-intra" active={active === 1} onClick={() => onSwitch(1)} icon={<Activity className="w-4 h-4" />} label="Part 1 · India" tone="yellow" />
          <SwitchBtn testid="main-switch-us" active={active === 2} onClick={() => onSwitch(2)} icon={<Zap className="w-4 h-4" />} label="Part 2 · US" tone="pink" />
          {user?.is_admin && (
            <SwitchBtn testid="main-switch-admin" active={active === 3} onClick={() => onSwitch(3)} icon={<Shield className="w-4 h-4" />} label="Admin" tone="blue" />
          )}
        </div>

        {/* User menu */}
        <div className="relative flex items-center gap-3" ref={ref}>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: `${indicatorColor}40`, background: `${indicatorColor}10` }}>
            <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: indicatorColor }} />
            <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: indicatorColor }}>{indicatorLabel}</span>
          </div>
          <button data-testid="user-menu-btn" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
            ) : (
              <UserIcon className="w-4 h-4 text-zinc-400" />
            )}
            <span className="hidden sm:inline text-xs text-zinc-300 max-w-[100px] truncate">{user?.name}</span>
          </button>
          {open && (
            <div data-testid="user-menu-dropdown" className="absolute top-full right-0 mt-2 min-w-[220px] bg-[#0F0F0F] border border-zinc-800 rounded-md shadow-xl overflow-hidden">
              <div className="p-3 border-b border-zinc-900">
                <div className="text-xs text-zinc-500">Signed in</div>
                <div className="text-sm truncate">{user?.email}</div>
                {user?.is_admin && <div className="text-[10px] font-mono uppercase tracking-widest text-[#F472B6] mt-1">Owner Admin</div>}
              </div>
              <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-white">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function SwitchBtn({ testid, active, onClick, icon, label, tone }) {
  const activeBg = tone === "pink"
    ? "bg-[#F472B6]/15 text-[#F472B6] border border-[#F472B6]/30"
    : tone === "blue"
      ? "bg-[#60A5FA]/15 text-[#60A5FA] border border-[#60A5FA]/30"
      : "bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all ${active ? activeBg : "text-zinc-400 hover:text-white"}`}
    >
      {icon} <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
